import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import FileForm, { FileFormType } from '../../components/Form/File/FileForm'
import { FileMutationDto, ScaffoldDto } from '../../config/fileConfig'
import Loading from '../../components/Loading'
import useEntity from '../../hooks/useEntity'
import useUser from '../../hooks/useUser'
import useAxios from '../../hooks/useAxios'
import { Billable } from '../../config/registration/timeConfig'
import useUnits from '../../hooks/useUnits'
import useModules from '../../hooks/module/useModules'
import { ModulesConstants } from '../../components/Form/File/ModulePivotItem'
import { useSettings } from '../../hooks/useSettings'
import { FileRateType, ModuleType } from '../../context/GlobalContext'
import useSurchargeTypes from '../../hooks/useSurchargeTypes'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'

export default function AddFile(): ReactElement {
    const axios = useAxios()

    const { getDefaultValues: applyDefaultAttributesValues } = useAttributes(
        AttributeViewType.Input,
        [AttributeContextType.File],
        undefined,
        EntityAttributeType.File,
    )
    const { currentUser } = useUser()
    const { activeUnits } = useUnits()
    const modules = useModules()
    const settings = useSettings()
    const { getSurchargeType, getSurchargeTypeName } = useSurchargeTypes()
    const [scaffold] = useEntity<ScaffoldDto>(() => ({ url: '/file/scaffold', method: 'POST' }), false)

    const initialValues: FileMutationDto = useMemo(() => {
        const values: FileMutationDto = {
            name: '',
            ownerId: currentUser?.id,
            ...scaffold?.data,

            fileRateType: scaffold?.data?.fileRateType || FileRateType.File,
            billable: scaffold?.data?.billable || Billable.Billable,
            currency: scaffold?.data?.currency || 'EUR',
            language: scaffold?.data?.language || 'nl',
            surcharges: scaffold?.data?.surcharges?.map(surcharge => {
                surcharge.name = getSurchargeTypeName(surcharge?.surchargeType)
                surcharge.value = getSurchargeType(surcharge?.surchargeType)?.value
                return surcharge
            }),
            unit: scaffold?.data?.unit || (modules?.has(ModuleType.Accounting) && activeUnits?.length === 1 ? activeUnits?.[0].key : undefined),
            workflows: modules?.has(ModuleType.Accounting) ? scaffold?.data?.workflows : undefined,
        }

        const fileModules = modules?.modules
            ?.filter(module => module.fileModule)
            ?.filter(module => module.defaultActiveForFile)
            .map(
                module =>
                    ModulesConstants.get(module.type)?.defaultValue(values, settings) ?? {
                        type: module.type,
                    },
            )
        if (fileModules && fileModules.length > 0) {
            values.modules = fileModules
        }

        modules?.modules?.filter(module => !module.fileModule).forEach(module => ModulesConstants.get(module.type)?.onAfterChecked(values, settings))

        applyDefaultAttributesValues(values)

        return values
    }, [currentUser, scaffold, modules, activeUnits])

    if (!scaffold.loaded) {
        return <Loading />
    }

    return (
        <FileForm
            formType={FileFormType.Add}
            formikProps={{ initialValues }}
            onSubmit={(file: FileMutationDto) => axios.post<string>('/file', file)}
        />
    )
}
